// Admin CRUD: origin_ports (list, add, edit, delete) — ports within each country/origin

import { useState } from 'react';
import { useOrigins, useOriginPorts } from '@/hooks/use-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { OriginPort } from '@/lib/store-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';

export default function AdminOriginPorts() {
  const queryClient = useQueryClient();
  const { data: origins } = useOrigins();
  const { data: ports, isLoading } = useOriginPorts(); // all ports
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OriginPort | null>(null);
  const [origin_id, setOrigin_id] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { toast } = useToast();

  const saveMutation = useMutation({
    mutationFn: async () => {
      const row = { origin_id, name: name.trim(), description: description.trim() || null };
      if (editing) {
        const { error } = await supabase.from('origin_ports').update(row).eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('origin_ports').insert(row);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'origin_ports'] });
      setOpen(false);
      setEditing(null);
      resetForm();
      toast({ title: editing ? 'Port updated' : 'Port created' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('origin_ports').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store', 'lookups', 'origin_ports'] });
      toast({ title: 'Port deleted' });
    },
    onError: (e) => toast({ title: 'Error', description: e instanceof Error ? e.message : 'Failed', variant: 'destructive' }),
  });

  function resetForm() {
    setOrigin_id(origins?.[0]?.id ?? '');
    setName('');
    setDescription('');
    setEditing(null);
  }

  function openEdit(port: OriginPort) {
    setEditing(port);
    setOrigin_id(port.origin_id);
    setName(port.name);
    setDescription(port.description ?? '');
    setOpen(true);
  }

  // Group ports by origin for better display
  const originMap = new Map((origins ?? []).map((o) => [o.id, o.name]));

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2">Origin Ports</h1>
      <p className="text-muted-foreground text-sm mb-6">Ports of departure for each origin country. One country can have multiple ports.</p>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogTrigger asChild>
          <Button onClick={() => { resetForm(); setEditing(null); }}>Add port</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit port' : 'New port'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Origin (Country)</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={origin_id}
                onChange={(e) => setOrigin_id(e.target.value)}
              >
                <option value="">Select origin...</option>
                {(origins ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Port Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Shanghai, Valencia" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Additional notes" />
            </div>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !name.trim() || !origin_id}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      {isLoading ? (
        <p className="text-secondary">Loading...</p>
      ) : (
        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Origin</TableHead>
              <TableHead>Port</TableHead>
              <TableHead>Description</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(ports ?? []).map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{originMap.get(p.origin_id) ?? '—'}</TableCell>
                <TableCell>{p.name}</TableCell>
                <TableCell className="text-muted-foreground">{p.description ?? '—'}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(p.id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
